import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// #273: Fremder HTML-Inhalt in einem abgeschotteten <iframe srcdoc> statt per
// dangerouslySetInnerHTML mitten in der Anwendung. Seit #285 traegt der Rahmen
// auch Teams-Nachrichten — er kennt nichts Mailspezifisches und hiess deshalb
// bis Sprint 6 zu eng EmailHtmlFrame.
//
// Zwei Probleme auf einmal:
//
// 1. Formatierung ging verloren. sanitizeExternalHtml gab nur doc.body.innerHTML
//    zurueck — der <head> samt <style> fiel weg. In einer Stichprobe von 500
//    Mails aus der Produktion tragen 431 einen <style>-Block, und bei 290 von
//    300 steht er im <head>. Diese Mails wurden also systematisch entstellt.
// 2. Die Mail lief im selben Dokument wie die Anwendung. Ihr CSS konnte in die
//    Oberflaeche wirken (und umgekehrt), und jede Luecke in der Bereinigung war
//    eine Luecke in der Anwendung selbst.
//
// Der Rahmen loest beides: die Mail bekommt ihr eigenes Dokument zurueck und
// kann es nicht verlassen.
//
// Zur Sandbox-Wahl:
//   - KEIN allow-scripts. Damit laeuft in der Mail ueberhaupt kein Skript.
//   - allow-same-origin ist noetig, um die Hoehe zu messen (ohne kommt der
//     Rahmen in einen eigenen Ursprung und contentDocument ist gesperrt).
//     Gefaehrlich waere die Kombination allow-scripts + allow-same-origin —
//     dann koennte sich der Inhalt selbst aus der Sandbox nehmen. Ohne Skripte
//     ist same-origin folgenlos.
//   - allow-popups(+-to-escape-sandbox), damit Links wie bisher in einem neuen
//     Tab aufgehen. allow-top-navigation fehlt bewusst: eine Mail darf die
//     Anwendung nicht wegnavigieren.
// Dazu eine CSP im Dokument als zweite Linie — sie verbietet Skripte auch dann,
// wenn die Sandbox einmal falsch gesetzt wuerde.
const SANDBOX = 'allow-same-origin allow-popups allow-popups-to-escape-sandbox';

// default-src 'none' ist die Grundhaltung; erlaubt wird nur, was eine Mail
// wirklich braucht. Bilder bleiben absichtlich offen — sie laden heute auch
// (Signaturen, Logos), und sie stillzulegen waere eine sichtbare
// Verhaltensaenderung, die ins Team gehoert und nicht in einen Bugfix.
const CSP = [
  "default-src 'none'",
  "img-src * data: blob:",
  "media-src * data: blob:",
  "style-src 'unsafe-inline'",
  "font-src data:",
  "script-src 'none'",
  "object-src 'none'",
  "frame-src 'none'",
  "form-action 'none'",
  "base-uri 'none'"
].join('; ');

// Neutrale Grundlage, damit ein Inhalt ohne eigenes CSS so aussieht wie bisher
// im Anwendungsfluss — sonst faellt er im Rahmen auf die Browser-Vorgaben
// (Times New Roman) zurueck.
const BASIS_CSS = `
  html, body { margin: 0; padding: 0; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #1f2328;
    background: transparent;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  img { max-width: 100%; height: auto; }
  a { color: #0078d4; }

  /* Die Anwendung hat frueher ueber .email-body-content p/a/table/blockquote
     mitgestaltet. Das faellt weg, sobald die Mail ihr eigenes Dokument hat —
     und genau das ist der Sinn von #273: Die Mail soll aussehen wie gedacht,
     nicht wie von uns uebermalt. Damit eine Mail OHNE eigenes CSS trotzdem
     ordentlich dasteht, kommen die neutralen Reste hier mit; die Verzierung
     von damals (Farbverlaeufe, Schlagschatten) bleibt draussen — die hat #268
     ohnehin aus der Anwendung entfernt. Jede Regel hier verliert gegen die
     Mail, weil deren eigene Stile spaeter im Dokument stehen. */
  p { margin: 0 0 12px 0; }
  blockquote {
    margin: 12px 0;
    padding: 12px 16px;
    border-left: 4px solid #c8c8c8;
  }
  table { max-width: 100%; border-collapse: collapse; }
  td, th { text-align: left; }
`;

const baueDokument = (html) => `<!doctype html>
<html><head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP}">
<base target="_blank">
<style>${BASIS_CSS}</style>
</head>
<body>${html || ''}</body></html>`;

const ExternalHtmlFrame = ({ html, title, minHeight = 120, className = '' }) => {
  const rahmenRef = useRef(null);
  const beobachterRef = useRef(null);
  const [hoehe, setHoehe] = useState(minHeight);

  const srcDoc = useMemo(() => baueDokument(html), [html]);

  const messen = useCallback(() => {
    const rahmen = rahmenRef.current;
    if (!rahmen) return;
    try {
      const doc = rahmen.contentDocument;
      const koerper = doc && doc.body;
      if (!koerper) return;
      // scrollHeight des Koerpers plus der Rest, den ein Wurzelelement mit
      // eigenem Rand beitragen kann.
      const gemessen = Math.max(
        koerper.scrollHeight,
        doc.documentElement ? doc.documentElement.scrollHeight : 0
      );
      if (gemessen > 0) setHoehe(Math.max(minHeight, gemessen));
    } catch (_) {
      // contentDocument gesperrt (sollte mit allow-same-origin nicht passieren)
    }
  }, [minHeight]);

  const beiLaden = useCallback(() => {
    messen();

    // Bilder und Webfonts kommen nach dem load-Ereignis nach und aendern die
    // Hoehe. Ein ResizeObserver auf dem Koerper faengt das zuverlaessiger ab
    // als ein fester Zeitgeber.
    try {
      const doc = rahmenRef.current && rahmenRef.current.contentDocument;
      const koerper = doc && doc.body;
      if (koerper && typeof ResizeObserver !== 'undefined') {
        if (beobachterRef.current) beobachterRef.current.disconnect();
        beobachterRef.current = new ResizeObserver(() => messen());
        beobachterRef.current.observe(koerper);
      }
      // Zusaetzlich je Bild, weil ein nachgeladenes Bild in einer Tabelle die
      // Koerperhoehe nicht immer neu ausloest.
      if (doc) {
        doc.querySelectorAll('img').forEach((bild) => {
          if (bild.complete) return;
          bild.addEventListener('load', messen, { once: true });
          bild.addEventListener('error', messen, { once: true });
        });
      }
    } catch (_) {}
  }, [messen]);

  useEffect(() => () => {
    if (beobachterRef.current) {
      beobachterRef.current.disconnect();
      beobachterRef.current = null;
    }
  }, []);

  // Neuer Inhalt: Hoehe zuruecksetzen, damit eine kurze Mail nach einer langen
  // nicht in einem riesigen leeren Rahmen steht.
  useEffect(() => { setHoehe(minHeight); }, [srcDoc, minHeight]);

  return (
    <iframe
      ref={rahmenRef}
      title={title}
      srcDoc={srcDoc}
      sandbox={SANDBOX}
      onLoad={beiLaden}
      className={className}
      style={{ width: '100%', height: `${hoehe}px`, border: 0, display: 'block', colorScheme: 'light' }}
    />
  );
};

export default ExternalHtmlFrame;
